import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'rsuite';
import { showToast } from '../../utils/toast';
import { useDeleteTimesheetEntry, useSaveTimesheetEntry } from '../../hooks/useTimesheet';
import { ENTRY_SOURCE, formatClock12, formatHM, parseDurationInput, secondsToHours } from '../../utils/timesheet';
import { ClockPicker, RdChip, TaskPicker, ValidationBanners, ValidationFlag, clockSpanSeconds } from './TimesheetParts';
import PeriodActionModal from './PeriodActionModal';
import EntryHistoryModal from './EntryHistory';
import OverlapFixModal from './OverlapFixModal';
import { clockToMinutes, minutesToClock, planOverlapFix } from './overlapFix';

/**
 * One day's entries as a list: the task and where it sits, the note, when it
 * ran and for how long. Each of those is edited in place - click the task, the
 * note, the start or the end time, or the length, and it turns into its
 * control, saving when you leave it. With overlapping entries turned off, a
 * change that would overlap is fitted around its neighbours instead of being
 * refused (overlapFix.js). The editors sit
 * over the space the text took, so opening one never changes the row's height.
 * The hover actions duplicate an entry in one click (same task, note and
 * length, starting where the day's last entry ended) or delete it, after a
 * confirm.
 *
 * Below the list, the day's span - first start to last end - and its total,
 * then every check the day fails, its entries' included, from the same rules
 * View Hours runs (dayIssues / entryIssues, from groupValidationIssues). An
 * entry that fails one also carries a mark. Checks the server can fix (a
 * missing meal break, an entry off the quarter hour) get a button, which
 * `onFix` applies.
 */
export default function DayView({
  entries,
  tasks,
  taskById,
  config,
  editable,
  highlightTaskId = null,
  dayIssues = [],
  entryIssues = null,
  onFix = null,
  fixing = null,
  onDuplicate,
  showHistory = false,
}) {
  const saveMutation = useSaveTimesheetEntry();
  const deleteMutation = useDeleteTimesheetEntry();
  // { id, field } of the one value being edited.
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [openNoteId, setOpenNoteId] = useState(null);
  // { entry, options, choice } when an entry is boxed in and the person picks
  // which neighbours move (see overlapFix.js).
  const [fixPlan, setFixPlan] = useState(null);
  // The entry whose save is in flight, so the task picker closing right after
  // a pick does not close the editor before the save lands.
  const savingId = useRef(null);

  const sorted = [...entries].sort((a, b) => (a.startTime || '99').localeCompare(b.startTime || '99'));
  const total = entries.reduce((s, e) => s + e.durationSeconds, 0);
  const starts = entries.map((e) => e.startTime).filter(Boolean).sort();
  const ends = entries.map((e) => e.endTime).filter(Boolean).sort();

  const exempt = new Set(config?.descriptionExemptRoles || []);
  const needsDescription = (task) => Boolean(config?.settings?.requireDescription) && !exempt.has(task?.systemRole);

  const stopEditing = () => setEditing(null);

  // The editor stays up, showing the new value, until the list has been
  // refetched - so the old value never flashes back in between.
  const save = async (entry, patch) => {
    const editSession = editing;
    savingId.current = entry.id;
    try {
      await saveMutation.mutateAsync({ id: entry.id, ...patch });
    } catch (err) {
      showToast('error', String(err?.message || err));
    } finally {
      savingId.current = null;
      setEditing((current) => (current === editSession ? null : current));
    }
  };

  const overlapsOff = config?.settings?.allowOverlaps === false;
  const lengthOf = (startTime, endTime) => (clockToMinutes(endTime) - clockToMinutes(startTime)) * 60;

  // Saves new times. With overlaps turned off, an entry that would cross
  // another is moved into the free time around it when it can be (`slide`,
  // for a length edit), or the person is offered ways to make room.
  const saveTimes = (entry, startTime, endTime, slide) => {
    const plan = overlapsOff ? planOverlapFix({ entries, entry, startTime, endTime, slide }) : { kind: 'clear' };
    if (plan.kind === 'clear') {
      save(entry, { startTime, endTime, durationSeconds: lengthOf(startTime, endTime) });
      return;
    }
    if (plan.kind === 'slide') {
      showToast('info', `Moved to ${formatClock12(plan.startTime)} – ${formatClock12(plan.endTime)} so it doesn't overlap.`);
      save(entry, { startTime: plan.startTime, endTime: plan.endTime, durationSeconds: lengthOf(plan.startTime, plan.endTime) });
      return;
    }
    stopEditing();
    if (plan.kind === 'stuck') {
      showToast('warning', "There's no room for that without overlapping, and the entries around it can't be moved.");
      return;
    }
    setFixPlan({ entry, options: plan.options, choice: plan.options[0].key });
  };

  // Neighbours first, in the order the plan gives, then the entry itself - so
  // no single save overlaps anything.
  const applyFixPlan = async () => {
    const option = fixPlan.options.find((o) => o.key === fixPlan.choice);
    try {
      for (const m of option.moves) {
        // eslint-disable-next-line no-await-in-loop
        await saveMutation.mutateAsync({ id: m.entry.id, startTime: m.startTime, endTime: m.endTime, durationSeconds: lengthOf(m.startTime, m.endTime) });
      }
      await saveMutation.mutateAsync({
        id: fixPlan.entry.id,
        startTime: option.startTime,
        endTime: option.endTime,
        durationSeconds: lengthOf(option.startTime, option.endTime),
      });
      setFixPlan(null);
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  const saveTime = (entry, field, clock) => {
    if (!clock || clock === entry[field]) {
      stopEditing();
      return;
    }
    const startTime = field === 'startTime' ? clock : entry.startTime;
    const endTime = field === 'endTime' ? clock : entry.endTime;
    const durationSeconds = clockSpanSeconds(startTime, endTime);
    if (durationSeconds === null) {
      showToast('warning', 'The end time has to be after the start time.');
      stopEditing();
      return;
    }
    saveTimes(entry, startTime, endTime, false);
  };

  const saveNote = (entry, text) => {
    const description = text.trim();
    if (description === (entry.description || '')) {
      stopEditing();
      return;
    }
    if (!description && needsDescription(taskById.get(entry.taskId))) {
      showToast('warning', 'Say what you worked on.');
      stopEditing();
      return;
    }
    save(entry, { description });
  };

  const saveTask = (entry, taskId) => {
    if (!taskId || taskId === entry.taskId) {
      stopEditing();
      return;
    }
    if (!entry.description && needsDescription(taskById.get(taskId))) {
      showToast('warning', 'That task needs a note. Add one first, then change the task.');
      stopEditing();
      return;
    }
    save(entry, { taskId });
  };

  const saveDuration = (entry, text) => {
    const durationSeconds = parseDurationInput(text);
    if (durationSeconds === entry.durationSeconds || !String(text).trim()) {
      stopEditing();
      return;
    }
    if (durationSeconds === null) {
      showToast('warning', 'Enter hours, like 2.5.');
      stopEditing();
      return;
    }
    if (!(entry.startTime && entry.endTime)) {
      save(entry, { durationSeconds });
      return;
    }
    // An entry with times keeps its start and moves its end to fit - or, if
    // that runs past midnight, keeps its end and starts earlier.
    const minutes = Math.round(durationSeconds / 60);
    let start = clockToMinutes(entry.startTime);
    if (start + minutes >= 24 * 60) start = clockToMinutes(entry.endTime) - minutes;
    if (minutes <= 0 || start < 0) {
      showToast('warning', minutes <= 0 ? 'The length has to be more than zero.' : "That's longer than the day has room for.");
      stopEditing();
      return;
    }
    saveTimes(entry, minutesToClock(start), minutesToClock(start + minutes), true);
  };

  const remove = async () => {
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
      showToast('success', 'Entry deleted.');
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  const duplicate = async (entry) => {
    setDuplicatingId(entry.id);
    try {
      await onDuplicate(entry);
    } finally {
      setDuplicatingId(null);
    }
  };

  const deleteTask = pendingDelete ? taskById.get(pendingDelete.taskId) : null;

  return (
    <section className="ts-day-section ts-agenda">
      {sorted.length > 0 ? (
        <div className="ts-agenda__head">
          <h2 className="ts-agenda__count">{sorted.length} {sorted.length === 1 ? 'entry' : 'entries'}</h2>
          <span className="ts-agenda__span">
            {starts.length && ends.length ? `${formatClock12(starts[0])} – ${formatClock12(ends[ends.length - 1])} · ` : ''}
            <strong>{formatHM(total)}</strong>
          </span>
        </div>
      ) : null}

      {sorted.length === 0 ? (
        <div className="ts-empty">
          <i className="bi bi-calendar2" aria-hidden="true" />
          Nothing logged on this day.
        </div>
      ) : (
        <ol className="ts-agenda__list">
          {sorted.map((entry, index) => {
            const task = taskById.get(entry.taskId);
            const root = task ? taskById.get(task.rootId) : null;
            const source = ENTRY_SOURCE[entry.source];
            const canChange = editable && !entry.locked;
            const parentPath = task && task.pathNames.length > 1 ? task.pathNames.slice(0, -1).join(' / ') : null;
            const field = editing?.id === entry.id ? editing.field : null;
            const edit = (name) => (canChange ? () => setEditing({ id: entry.id, field: name }) : undefined);
            const color = task?.color || root?.color || '#75777d';

            const taskBody = (
              <span className="ts-agenda__title">
                {parentPath ? <span className="ts-agenda__tag">{parentPath}</span> : null}
                <span className="ts-inline__text ts-agenda__task">{task?.name || 'Unknown task'}</span>
                {entry.billable ? <span className="ts-agenda__billable">Billable</span> : null}
                <ValidationFlag issues={entryIssues?.get(entry.id)} />
              </span>
            );

            return (
              <li
                key={entry.id}
                className={`ts-agenda__row${entry.taskId === highlightTaskId ? ' is-highlight' : ''}${index === 0 ? ' is-first' : ''}${index === sorted.length - 1 ? ' is-last' : ''}`}
                style={{ '--ts-chip': color }}
              >
                <div className="ts-agenda__time">
                  {entry.startTime && entry.endTime ? (
                    <>
                      {field === 'start' ? (
                        <InlineClock value={entry.startTime} label="Start time" onCommit={(clock) => saveTime(entry, 'startTime', clock)} onCancel={stopEditing} />
                      ) : (
                        <InlineButton enabled={canChange} onClick={edit('start')} label="Edit the start time">
                          <span className="ts-inline__text ts-agenda__start">{formatClock12(entry.startTime)}</span>
                        </InlineButton>
                      )}
                      {field === 'end' ? (
                        <InlineClock value={entry.endTime} anchor={entry.startTime} label="End time" onCommit={(clock) => saveTime(entry, 'endTime', clock)} onCancel={stopEditing} />
                      ) : (
                        <InlineButton enabled={canChange} onClick={edit('end')} label="Edit the end time">
                          <span className="ts-inline__text ts-agenda__end">{formatClock12(entry.endTime)}</span>
                        </InlineButton>
                      )}
                    </>
                  ) : <span className="ts-agenda__end">No times</span>}
                </div>

                <div className="ts-agenda__rail" aria-hidden="true"><span className="ts-agenda__dot" /></div>

                <div className="ts-agenda__body">
                  {field === 'task' ? (
                    <div className="ts-agenda__titleedit">
                      {/* Holds the line at the height of the title it replaces. */}
                      <span className="ts-inline__keep" aria-hidden="true">{taskBody}</span>
                      <div className="ts-inline__over">
                        <TaskPicker
                          tasks={tasks}
                          value={entry.taskId}
                          onChange={(taskId) => saveTask(entry, taskId)}
                          onClose={() => {
                            if (savingId.current !== entry.id) stopEditing();
                          }}
                          cleanable={false}
                          defaultOpen
                          size="sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <InlineButton enabled={canChange} onClick={edit('task')} label={`Change the task for ${task?.path || 'this entry'}`} block>
                      {taskBody}
                    </InlineButton>
                  )}

                  <div
                    className={`ts-agenda__note${entry.description ? '' : ' is-empty'}${field === 'note' ? ' is-editing' : ''}`}
                  >
                    {field === 'note' ? (
                      <InlineText
                        initial={entry.description || ''}
                        placeholder={needsDescription(task) ? 'note' : 'note (optional)'}
                        label="Note"
                        multiline
                        onCommit={(text) => saveNote(entry, text)}
                        onCancel={stopEditing}
                      />
                    ) : canChange || !entry.description ? (
                      <InlineButton enabled={canChange} onClick={edit('note')} label="Edit the note">
                        <span className="ts-inline__text">{entry.description || 'Add a note'}</span>
                      </InlineButton>
                    ) : (
                      // Read-only: a click shows the whole note, wrapped.
                      <button
                        type="button"
                        className={`ts-inline ts-inline--block${openNoteId === entry.id ? ' is-open' : ''}`}
                        aria-expanded={openNoteId === entry.id}
                        onClick={() => setOpenNoteId(openNoteId === entry.id ? null : entry.id)}
                      >
                        <span className="ts-inline__text">{entry.description}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="ts-agenda__side">
                  {/* Only entries that count toward the R&D credit say so. */}
                  {entry.rdEligible ? (
                    <RdChip
                      on={entry.rdEligible}
                      onToggle={canChange ? () => save(entry, { rdEligible: !entry.rdEligible }) : null}
                    />
                  ) : null}
                  {source && entry.source !== 'timecamp_import' ? <i className={`bi ${source.icon} ts-source`} title={source.label} aria-label={source.label} /> : null}
                  <span className={`ts-agenda__length${field === 'duration' ? ' is-editing' : ''}`}>
                    {field === 'duration' ? (
                      <InlineText
                        initial={String(secondsToHours(entry.durationSeconds))}
                        placeholder="2.5"
                        label="Hours"
                        inputMode="decimal"
                        sanitize={(raw) => raw.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 5)}
                        suffix="h"
                        onCommit={(text) => saveDuration(entry, text)}
                        onCancel={stopEditing}
                      />
                    ) : (
                      // With times, a new length moves the end time to fit.
                      <InlineButton enabled={canChange} onClick={edit('duration')} label="Edit the hours">
                        <span className="ts-inline__text">{formatHM(entry.durationSeconds)}</span>
                      </InlineButton>
                    )}
                  </span>
                  <div className="ts-agenda__actions">
                    {/* An entry's history is for admins only. */}
                    {showHistory ? (
                      <button type="button" className="ts-toolicon ts-toolicon--sm" onClick={() => setHistoryFor(entry.id)} title="History" aria-label="History">
                        <i className="bi bi-clock-history" aria-hidden="true" />
                      </button>
                    ) : null}
                    {canChange ? (
                      <>
                        <button
                          type="button"
                          className="ts-toolicon ts-toolicon--sm"
                          onClick={() => duplicate(entry)}
                          disabled={duplicatingId === entry.id}
                          title="Duplicate"
                          aria-label="Duplicate"
                        >
                          <i className="bi bi-copy" aria-hidden="true" />
                        </button>
                        <button type="button" className="ts-toolicon ts-toolicon--sm ts-toolicon--danger" onClick={() => setPendingDelete(entry)} title="Delete" aria-label="Delete">
                          <i className="bi bi-trash3" aria-hidden="true" />
                        </button>
                      </>
                    ) : (
                      entry.locked ? <i className="bi bi-lock ts-source" title={source?.label || 'Locked'} aria-label="Locked" /> : null
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <ValidationBanners issues={dayIssues} onFix={editable ? onFix : null} fixing={fixing} />

      <EntryHistoryModal entryId={historyFor} onClose={() => setHistoryFor(null)} />

      <OverlapFixModal
        plan={fixPlan}
        taskById={taskById}
        onChoose={(choice) => setFixPlan((p) => ({ ...p, choice }))}
        onApply={applyFixPlan}
        onClose={() => setFixPlan(null)}
        loading={saveMutation.isPending}
      />

      <PeriodActionModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
        title="Delete Time Entry"
        confirmLabel="Delete"
        confirmColor="red"
        showComment={false}
        loading={deleteMutation.isPending}
      >
        {pendingDelete ? (
          <p className="mb-0">
            Delete {formatHM(pendingDelete.durationSeconds)} on <strong>{deleteTask?.path || 'this task'}</strong>
            {pendingDelete.startTime && pendingDelete.endTime
              ? ` (${formatClock12(pendingDelete.startTime)} – ${formatClock12(pendingDelete.endTime)})`
              : ''}
            ? This cannot be undone.
          </p>
        ) : null}
      </PeriodActionModal>
    </section>
  );
}

// A value that turns into its editor on click; plain text when it cannot be
// changed. The underline on hover is the hint that it can.
function InlineButton({ enabled, onClick, label, block = false, children }) {
  if (!enabled) return block ? <div>{children}</div> : <span className="ts-inline--static">{children}</span>;
  return (
    <button type="button" className={`ts-inline${block ? ' ts-inline--block' : ''}`} onClick={onClick} aria-label={label} title={label}>
      {children}
    </button>
  );
}

// Enter or leaving the field saves; Escape puts it back. Multiline grows to
// fit, so a long note shows in full while it is being edited.
function InlineText({ initial, placeholder, label, inputMode, multiline = false, sanitize, suffix, onCommit, onCancel }) {
  const [text, setText] = useState(initial);
  const [done, setDone] = useState(false);
  const fieldRef = useRef(null);
  useEffect(() => {
    const el = fieldRef.current;
    if (!multiline || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [multiline, text]);
  const finish = (keep) => {
    if (done) return;
    setDone(true);
    if (keep) onCommit(text);
    else onCancel();
  };
  const field = (
    <Input
      ref={fieldRef}
      as={multiline ? 'textarea' : undefined}
      rows={multiline ? 1 : undefined}
      className={`ts-inline__edit${multiline ? ' ts-inline__edit--multi' : ''}`}
      size="sm"
      value={text}
      onChange={(value) => setText(sanitize ? sanitize(value) : value)}
      onFocus={multiline ? undefined : (e) => e.target.select()}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finish(false);
        }
      }}
      placeholder={placeholder}
      aria-label={label}
      inputMode={inputMode}
      readOnly={done}
      autoFocus
    />
  );
  if (!suffix) return field;
  return (
    <span className="ts-inline__suffixed">
      {field}
      <span className="ts-inline__suffix" aria-hidden="true">{suffix}</span>
    </span>
  );
}

// The time picker, opened on the hour. It saves when the picker closes (OK,
// Enter, a click elsewhere) or focus tabs away to something else on the page;
// Escape puts it back. The last typed value is kept in a ref, because rsuite
// can report the change and close in the same tick.
function InlineClock({ value, anchor = null, label, onCommit, onCancel }) {
  const wrapRef = useRef(null);
  const draft = useRef(value);
  const done = useRef(false);
  const [shown, setShown] = useState(value);

  const finish = (keep) => {
    if (done.current) return;
    done.current = true;
    if (keep) onCommit(draft.current);
    else onCancel();
  };

  useEffect(() => {
    wrapRef.current?.querySelector('input')?.focus();
  }, []);

  return (
    <span
      ref={wrapRef}
      className="ts-inline__clock ts-inline__edit"
      onKeyDownCapture={(e) => {
        if (e.key === 'Escape') finish(false);
      }}
      onBlur={(e) => {
        // Moving between the hour, minute and AM/PM is not leaving it; only
        // focus landing on something else is. Clicks elsewhere arrive as onClose.
        const next = e.relatedTarget;
        if (next && !wrapRef.current?.contains(next) && !next.closest('.ts-clock-panel')) finish(true);
      }}
    >
      <ClockPicker
        value={shown}
        onChange={(clock) => {
          draft.current = clock;
          setShown(clock);
        }}
        onClose={() => finish(true)}
        anchor={anchor}
        cleanable={false}
        size="sm"
        aria-label={label}
      />
    </span>
  );
}
